<template>
  <iColumn
    v-if="previewsBothStates"
    gap="extraSmall"
    width="fill"
    height="hug"
    data-test="checkbox-state-preview"
  >
    <AppComponentOverrideHost
      v-for="componentId in previewComponentIds"
      :key="componentId"
      :app-name="questionnaireAppName"
      :dynamic-component-id="componentId"
      :contexts="previewContexts"
      :theme-mode="themeMode"
    />
  </iColumn>
  <AppComponentOverrideHost
    v-else-if="resolvedComponentId && resolvedContexts"
    :app-name="questionnaireAppName"
    :dynamic-component-id="resolvedComponentId"
    :contexts="resolvedContexts"
    :theme-mode="themeMode"
  />
  <iText
    v-else-if="editMode"
    variant="subtle"
    font-size="small"
    data-test="checkbox-state-empty"
  >
    {{ emptyStateMessage }}
  </iText>
</template>

<script>
import App from "@bloglovin/inspire-engine/App";
import AppComponentOverrideHost
  from "%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/AppComponentOverrideHost.vue";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import { resolveBoundEntityId } from "%/dynamic-component-editor/helpers/bound-context-entity-id";
import { declaredAppOption, declaredAppOptions } from "%/dynamic-component-editor/options/declared-app-options";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import { CHECKBOX_STATES_OPTION_KEY } from "+/QuizzesPolls/Components/questionnaire-card-selection";
import { QUIZZES_POLLS_APP_NAME } from "+/QuizzesPolls/app-name";

const CHECKED_CARD_KEY = "checkbox-card-checked";
const UNCHECKED_CARD_KEY = "checkbox-card-unchecked";

const ANSWER_CONTEXT_NAME = "answer";
const ANSWER_ENTITY_TYPE = "Answer";
const CHECKED_VALUE = "true";
const DEFAULT_APP_NAME = "QuizzesPolls";

const MSG_NO_CONTEXT = "Drop this inside a question card, so it has an answer to read.";
const MSG_NO_CHECKED = "No component set for the checked state.";
const MSG_NO_UNCHECKED = "No component set for the unchecked state.";

export default {
  name: "CheckboxStateElement",
  components: { AppComponentOverrideHost },
  mixins: [DynamicElementMixin],
  inject: {
    pageEntities: { default: null },
    scopedProvidedContexts: { default: null },
  },
  elementPanelSettings: {
    displayName: "Checkbox State",
    displayIcon: "mdi:checkbox-marked-outline",
    elementType: "element",
    elementGroup: "general",
  },
  options: [
    ...declaredAppOptions(QUIZZES_POLLS_APP_NAME, [
      CHECKBOX_STATES_OPTION_KEY,
      CHECKED_CARD_KEY,
      UNCHECKED_CARD_KEY,
    ]),
  ],
  optionBundles: [new AppearanceOptions()],
  computed: {
    questionnaireAppName() {
      return this.elementContext?.appName || DEFAULT_APP_NAME;
    },
    questionnaireApp() {
      try {
        return App.get(this.questionnaireAppName);
      } catch {
        return null;
      }
    },
    elementContext() {
      return this.optionValues?.context || this.configuration?.options?.context || {};
    },
    answerEntityId() {
      return resolveBoundEntityId(this, ANSWER_CONTEXT_NAME) || 0;
    },
    isChecked() {
      return this.answerField("value") === CHECKED_VALUE;
    },
    resolvedComponentId() {
      return this.componentIdForKey(this.isChecked ? CHECKED_CARD_KEY : UNCHECKED_CARD_KEY);
    },
    previewsBothStates() {
      return Boolean(this.editMode) && !this.resolvedContexts && this.previewComponentIds.length > 0;
    },
    previewComponentIds() {
      return [CHECKED_CARD_KEY, UNCHECKED_CARD_KEY]
          .map(key => this.componentIdForKey(key))
          .filter(componentId => componentId > 0);
    },
    previewContexts() {
      return {
        [ANSWER_CONTEXT_NAME]: { entityType: ANSWER_ENTITY_TYPE, pk: { id: 0 } },
      };
    },
    resolvedContexts() {
      if (!this.answerEntityId) {
        return null;
      }
      return {
        [ANSWER_CONTEXT_NAME]: {
          entityType: ANSWER_ENTITY_TYPE,
          pk: { id: this.answerEntityId },
        },
      };
    },
    emptyStateMessage() {
      if (!this.resolvedContexts) {
        return MSG_NO_CONTEXT;
      }
      return this.isChecked ? MSG_NO_CHECKED : MSG_NO_UNCHECKED;
    },
  },
  methods: {
    componentIdForKey(key) {
      const optionClass = declaredAppOption(QUIZZES_POLLS_APP_NAME, key);
      if (!optionClass) {
        return 0;
      }
      return Number(this.getOptionValue(optionClass)) || 0;
    },
    answerField(fieldName) {
      if (!this.answerEntityId || !this.questionnaireApp) {
        return null;
      }
      try {
        return this.questionnaireApp.getEntityField(ANSWER_ENTITY_TYPE, { id: this.answerEntityId }, fieldName);
      } catch {
        return null;
      }
    },
  },
};
</script>
